import React, { useState, useEffect, useRef } from 'react'
import {
  Plus,
  FolderOpen,
  Settings,
  TrendingUp,
  Layers,
  Users,
  Tag
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import projectCategoryMasterService from '../../services/projectCategoryMasterService'
import RevisionHistoryModal from '../../components/RevisionHistoryModal/RevisionHistoryModal'
import '../../components/RevisionHistoryModal/RevisionHistoryModal.css'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const ProjectCategoryMaster = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalCategories: 0,
    activeCategories: 0,
    inactiveCategories: 0,
    primaryCategories: 0,
    secondaryCategories: 0,
    subCategories: 0,
    defaultCategories: 0,
  })
  const [showRevisionHistory, setShowRevisionHistory] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [selectedCategoryData, setSelectedCategoryData] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [categoriesResponse, statsResponse] = await Promise.all([
        projectCategoryMasterService.getAllProjectCategoryMasters(),
        projectCategoryMasterService.getProjectCategoryMasterStats()
      ])

      if (categoriesResponse.success !== false && categoriesResponse.data) {
        const transformedData = (categoriesResponse.data || []).map(cat => ({
          ...cat,
          id: cat._id,
          formattedBudget: cat.budgetAllocation?.defaultBudget ? 
            `${cat.budgetAllocation.currency} ${cat.budgetAllocation.defaultBudget.toLocaleString('en-IN')}` : 'N/A',
          formattedParentCategory: cat.parentCategory?.categoryName || 'None',
          currentVersion: `Rev ${cat.currentRevision || 1}`,
          categoryTypeIcon: getCategoryTypeIcon(cat.categoryType),
          categoryTypeColor: getCategoryTypeColor(cat.categoryType)
        }))
        setData(transformedData)
        if (categoriesResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No project category records found. Create your first project category!')
        }
      } else if (!categoriesResponse._errorHandled) {
        toast.error(categoriesResponse.message || 'Failed to load project category master records')
      }

      if (statsResponse.success !== false && statsResponse.data) {
        setStats(statsResponse.data)
      } else if (!statsResponse._errorHandled && statsResponse.message) {
        // Only show stats error if it's not a network error (which is already shown by interceptor)
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading project category master records:', error)
      // Only show error if it hasn't been handled by the interceptor
      if (!error._errorHandled) {
        if (error.response?.status === 401) {
          toast.error('Authentication required. Please log in again.')
        } else if (error.response?.status === 403) {
          toast.error('You do not have permission to view project category master records')
        } else if (error.response) {
          toast.error('Failed to load project category master records')
        }
        // Network errors are already handled by the interceptor, so we don't show them again
      }
    } finally {
      setLoading(false)
    }
  }

  const getCategoryTypeIcon = (type) => {
    switch (type) {
      case 'Primary': return <Layers size={14} className="text-blue-600" />
      case 'Secondary': return <Tag size={14} className="text-green-600" />
      case 'Sub-Category': return <FolderOpen size={14} className="text-purple-600" />
      default: return <Layers size={14} className="text-gray-600" />
    }
  }

  const getCategoryTypeColor = (type) => {
    switch (type) {
      case 'Primary': return 'bg-blue-100 text-blue-800'
      case 'Secondary': return 'bg-green-100 text-green-800'
      case 'Sub-Category': return 'bg-purple-100 text-purple-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const columns = [
    {
      key: 'categoryCode',
      label: 'Category Code',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: row.categoryColor + '20' }}>
            <span style={{ color: row.categoryColor, fontSize: '12px', fontWeight: '600' }}>
              {row.categoryIcon?.charAt(0).toUpperCase() || 'F'}
            </span>
          </div>
          <span className="font-medium text-gray-900">{value}</span>
        </div>
      )
    },
    {
      key: 'categoryName',
      label: 'Category Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          {row.categoryDescription && (
            <div className="text-sm text-gray-500">{row.categoryDescription}</div>
          )}
        </div>
      )
    },
    {
      key: 'categoryType',
      label: 'Type',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          {row.categoryTypeIcon}
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${row.categoryTypeColor}`}>
            {value}
          </span>
        </div>
      )
    },
    {
      key: 'formattedParentCategory',
      label: 'Parent Category',
      sortable: true,
      render: (value) => (
        <div className="text-sm text-gray-600">
          {value}
        </div>
      )
    },
    {
      key: 'formattedBudget',
      label: 'Default Budget',
      sortable: true,
      render: (value) => (
        <div className="text-sm text-gray-600">
          {value}
        </div>
      )
    },
    {
      key: 'categoryStatus',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value}
        </span>
      )
    }
  ]

  const handleAddNew = () => {
    navigate('/projects/project-category-master/new')
  }

  const handleEdit = (row) => {
    // Clean the data to remove non-serializable objects
    const cleanRowData = {
      _id: row._id,
      id: row.id,
      company: row.company,
      categoryCode: row.categoryCode,
      categoryName: row.categoryName,
      categoryDescription: row.categoryDescription,
      categoryType: row.categoryType,
      parentCategory: row.parentCategory,
      categoryStatus: row.categoryStatus,
      categoryColor: row.categoryColor,
      categoryIcon: row.categoryIcon,
      isDefault: row.isDefault,
      sortOrder: row.sortOrder,
      applicableProjectTypes: row.applicableProjectTypes,
      requiresApproval: row.requiresApproval,
      budgetAllocation: row.budgetAllocation,
      timelineSettings: row.timelineSettings,
      resourceRequirements: row.resourceRequirements,
      qualityStandards: row.qualityStandards,
      revisionHistory: row.revisionHistory,
      currentRevision: row.currentRevision,
      createdBy: row.createdBy,
      updatedBy: row.updatedBy,
      isArchived: row.isArchived,
      createdAt: row.createdAt,
      updatedAt: row.updatedAt
    }
    
    navigate('/projects/project-category-master/edit', { 
      state: { categoryData: cleanRowData } 
    })
  }

  const handleView = (row) => {
    // Implement view logic if needed, or reuse edit for view-only mode
    toast.info(`Viewing Project Category: ${row.categoryName}`)
  }

  const handleDelete = (row) => {
    setDeleteItem(row)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const response = await projectCategoryMasterService.deleteProjectCategoryMaster(deleteItem.id)

      if (response.success) {
        toast.success('Project category deleted successfully')
        loadData()
        setShowDeleteModal(false)
        setDeleteItem(null)
      } else {
        toast.error(response.message || 'Failed to delete project category')
      }
    } catch (error) {
      console.error('Error deleting project category:', error)
      toast.error('Failed to delete project category')
    }
  }

  const handleRevisionHistory = (row) => {
    setSelectedCategoryData(row)
    setShowRevisionHistory(true)
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Projects',
      path: '/projects',
      onClick: () => navigate('/projects')
    },
    {
      label: 'Masters',
      path: '/projects',
      onClick: () => navigate('/projects')
    },
    {
      label: 'Project Category Master',
      path: '/projects/project-category-master',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Project Category Master</h1>
          <p>Manage project categories and hierarchies for your organization</p>
        </div>
        <div className="page-actions">
          <button
            className="btn-primary"
            onClick={handleAddNew}
          >
            <Plus size={16} />
            Add New
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <FolderOpen size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+15%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalCategories}</div>
          <div className="metric-title">Total Categories</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Settings size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{stats.activeCategories}</div>
          <div className="metric-title">Active Categories</div>
          <div className="metric-subtitle">Currently tracking</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Layers size={16} />
            </div>
            <div className="metric-trend neutral">
              <TrendingUp size={12} />
              <span>Stable</span>
            </div>
          </div>
          <div className="metric-value">{stats.primaryCategories}</div>
          <div className="metric-title">Primary Categories</div>
          <div className="metric-subtitle">Main categories</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{stats.secondaryCategories}</div>
          <div className="metric-title">Secondary Categories</div>
          <div className="metric-subtitle">Sub-categories</div>
        </div>
      </div>

      {/* Project Category Master Table */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onRevisionHistory={handleRevisionHistory}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search project categories..."
          emptyMessage="No project categories found. Create your first project category!"
          noCard={true}
        />
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Project Category"
        message={`Are you sure you want to delete project category "${deleteItem?.categoryName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Revision History Modal */}
      {selectedCategoryData && (
        <RevisionHistoryModal
          isOpen={showRevisionHistory}
          onClose={() => setShowRevisionHistory(false)}
          revisionHistory={selectedCategoryData.revisionHistory}
          dataName={selectedCategoryData.categoryName}
        />
      )}
    </div>
  )
}

export default ProjectCategoryMaster
